/**
 * EN-11109: rematch registrations in flight, per next tournament, shared by
 * every table that hosts a rematch Confirm.
 *
 * Two finished Sit & Go tables can offer the same next table. The engine takes
 * one registration per player and tournament, and its reply carries no request
 * id, so two Confirms for one tournament could not tell their replies apart.
 * The first Confirm for a tournament claims it and sends the registration; a
 * later Confirm for the same tournament sends nothing and waits for the
 * claim's outcome instead.
 *
 * An entry is cleared when its owner settles it (opened, refused, failed or moved),
 * when the owner releases it (unmount or close), or after
 * REMATCH_REGISTRATION_TIMEOUT_MS, so a lost reply never holds it. Clearing
 * without an outcome tells the waiters 'abandoned'.
 *
 * Module state rather than component state: each table host is its own
 * component instance.
 */

/**
 * A failed registration, already worded for the failure screen. `title`
 * replaces its "Registration Failed!" heading (a table that filled up).
 */
export type RematchRegistrationFailure = {
  message: string;
  insufficientFunds: boolean;
  title?: string;
};

export type RematchRegistrationOutcome =
  /** Registered, and the next table opened: retire the finished table. */
  | { kind: 'opened' }
  /** Registered, but the next table could not open: keep the finished table. */
  | { kind: 'refused' }
  /** The engine refused the registration. */
  | { kind: 'failed'; failure: RematchRegistrationFailure }
  /**
   * The table filled up first, and the owner moved its registration on to
   * another table of the template: nothing was registered here.
   */
  | { kind: 'moved' }
  /** No outcome will come: the owner went away or the reply never came. */
  | { kind: 'abandoned' };

type Waiter = (outcome: RematchRegistrationOutcome) => void;

type Flight = { owner: object; waiters: Set<Waiter>; timer: ReturnType<typeof setTimeout> };

/** Longer than any normal reply; only a lost one reaches it. */
export const REMATCH_REGISTRATION_TIMEOUT_MS = 15_000;

const flights = new Map<string, Flight>();

const finish = (tournamentId: string, outcome: RematchRegistrationOutcome) => {
  const flight = flights.get(tournamentId);
  if (!flight) return;
  flights.delete(tournamentId);
  clearTimeout(flight.timer);
  flight.waiters.forEach((waiter) => waiter(outcome));
};

/**
 * Claim the registration for `tournamentId`. True when `owner` now holds it
 * and should send the registration; false when another host's is in flight.
 */
export const claimRematchRegistration = (tournamentId: string, owner: object): boolean => {
  const held = flights.get(tournamentId);
  if (held) return held.owner === owner;
  flights.set(tournamentId, {
    owner,
    waiters: new Set(),
    timer: setTimeout(
      () => finish(tournamentId, { kind: 'abandoned' }),
      REMATCH_REGISTRATION_TIMEOUT_MS
    )
  });
  return true;
};

/**
 * Wait for the in-flight registration for `tournamentId`. Returns the
 * unsubscribe, or null when none is in flight.
 */
export const awaitRematchRegistration = (
  tournamentId: string,
  waiter: Waiter
): (() => void) | null => {
  const flight = flights.get(tournamentId);
  if (!flight) return null;
  flight.waiters.add(waiter);
  return () => {
    flight.waiters.delete(waiter);
  };
};

/** The owner's outcome, passed to every waiter. Ignored from a non-owner. */
export const settleRematchRegistration = (
  tournamentId: string,
  owner: object,
  outcome: Exclude<RematchRegistrationOutcome, { kind: 'abandoned' }>
) => {
  if (flights.get(tournamentId)?.owner !== owner) return;
  finish(tournamentId, outcome);
};

/** The owner is going away without an outcome: its waiters are told 'abandoned'. */
export const releaseRematchRegistration = (tournamentId: string, owner: object) => {
  if (flights.get(tournamentId)?.owner !== owner) return;
  finish(tournamentId, { kind: 'abandoned' });
};

/** Whether a registration for `tournamentId` is in flight (tests and logs). */
export const isRematchRegistrationInFlight = (tournamentId: string): boolean =>
  flights.has(tournamentId);
